/** Click identifiers are read only on explicit opt-in, never cached in browser storage. */
export interface SignupAttribution {
  advertisingConsent: true;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  withdrawalKey: string;
}
export const WITHDRAWAL_STORAGE_KEY = 'email-verification-measurement-withdrawal';
const KEY_FORMAT = /^[a-f0-9]{64}$/;
/**
 * One removal key per browser, reused for every consenting signup from it. Only a removal capability is
 * kept, never the identifiers or a consent inference.
 *
 * Never rotate a valid stored key. A submission can reach the server and still look failed to the user
 * (lost response, timeout); if each attempt minted a new key, the retry would overwrite the one the server
 * already hashed, and a later withdrawal would send a key that matches nothing - the server answers 204 to
 * unknown keys on purpose, so the page would report "withdrawn" while the consent row stayed active.
 * Consequence, intended: a later signup of another account in this browser carries the same key, so one
 * withdrawal here opts out every account signed up from it. A withdrawal can only remove data.
 *
 * A key that cannot be stored and read back is never returned: sending a key the user cannot later present
 * would create consent with no way to withdraw it.
 */
function stableWithdrawalKey(): string | undefined {
  const stored = localStorage.getItem(WITHDRAWAL_STORAGE_KEY);
  if (stored && KEY_FORMAT.test(stored)) return stored;
  const bytes = window.crypto.getRandomValues(new Uint8Array(32));
  const fresh = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  localStorage.setItem(WITHDRAWAL_STORAGE_KEY, fresh);
  return localStorage.getItem(WITHDRAWAL_STORAGE_KEY) === fresh ? fresh : undefined;
}
export function signupAttribution(consent: boolean): SignupAttribution | undefined {
  if (!consent) return undefined;
  try {
    const params = new URLSearchParams(window.location.search);
    const ids: {gclid?: string; gbraid?: string; wbraid?: string} = {};
    for (const name of ['gclid', 'gbraid', 'wbraid'] as const) {
      const values = params.getAll(name);
      if (values.length > 1) return undefined;
      if (values.length === 1) {
        if (!/^[A-Za-z0-9_-]{1,256}$/.test(values[0])) return undefined;
        ids[name] = values[0];
      }
    }
    if (!Object.keys(ids).length) return undefined;
    const withdrawalKey = stableWithdrawalKey();
    if (!withdrawalKey) return undefined;
    return {advertisingConsent: true, ...ids, withdrawalKey};
  } catch { return undefined; }
}
